import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import { Button } from '../../../../../../ui';
import AsyncCustomSelect from '../../../../../../ui/custom_react_select/async_select';
import { CustomSelect } from '../../../../../../ui/custom_react_select/custom_select';
import { DatePicker } from '../../../../../../ui/input/date_picker/date_picker';
import { TextInput } from '../../../../../../ui/input/text_input/text_input';
import useDebouncedJobTitleSearch from '../../../../../../utils/helpers/AsyncSelect/JobTitleGet';
import { JobTitle } from '../../../../../../utils/types/admin_types';
import {
  JobDescription,
  JobPostingTabData,
  workArrangement,
} from '../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';

type JobPostSettingsProps = {
  jobTitleList: JobTitle[];
  jobPostingData: JobPostingTabData;
  setJobPostingData: React.Dispatch<React.SetStateAction<JobPostingTabData>>;
  setActiveTab: (value: ((prevState: string) => string) | string) => void;
  jobDescription: JobDescription;
  setJobPostId: (value: ((prevState: string) => string) | string) => void;
};

const JobPostSettings = ({
  jobPostingData,
  setJobPostingData,
  jobTitleList,
  setActiveTab,
  jobDescription,
  setJobPostId,
}: JobPostSettingsProps) => {
  const { t } = useTranslation(NS.adminPages);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const [initialData, setInitialData] =
    useState<JobPostingTabData>(jobPostingData);
  const { id } = useParams<{ id: string }>();

  const workTypes: SelectOptions[] = [
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.hybrid), value: 'Hybrid' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.remote), value: 'Remote' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.office), value: 'Office' },
  ];

  const { loadOptionsDebounced } = useDebouncedJobTitleSearch();

  const changeDate = (timeRange: any) => {
    setJobPostingData({
      ...jobPostingData,
      startDate: timeRange.startDate,
      endDate: timeRange.endDate,
    });
  };

  const defaultJobTitles = jobTitleList.map((jobTitle) => ({
    value: jobTitle.id,
    label: jobTitle.jobTitle,
  }));

  const handleJobTitleChange = (selectedOptions: SelectOptions) => {
    setJobPostingData({ ...jobPostingData, jobTitle: selectedOptions.label });
  };

  const handleChange = (selectedOption: SelectOptions) => {
    const workType: workArrangement = selectedOption.value as workArrangement;
    setJobPostingData({ ...jobPostingData, workType: workType });
  };

  const handleToggle = () => {
    setJobPostingData({ ...jobPostingData, active: !jobPostingData.active });
  };

  const handleSave = () => {
    setInitialData(jobPostingData);
    setIsSaveButtonDisabled(true);
    setActiveTab('jobPostDetails');
  };

  useEffect(() => {
    const isChanged =
      JSON.stringify(jobPostingData) !== JSON.stringify(initialData);
    const allFieldsSet =
      !!jobPostingData.location &&
      !!jobPostingData.startDate &&
      !!jobPostingData.endDate &&
      !!jobPostingData.jobTitle &&
      !!jobPostingData.workType &&
      !!jobPostingData.department &&
      typeof jobPostingData.active === 'boolean';

    setIsSaveButtonDisabled(!(isChanged && allFieldsSet));
  }, [jobPostingData, initialData]);

  return (
    <div className='relative flex min-w-0 flex-col gap-3 p-3'>
      <div className='rounded-lg border border-slate-200 bg-white p-3 shadow-sm'>
        <div className='mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h3 className='text-sm font-extrabold text-slate-900'>
              { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.tabs.settings) }
            </h3>
          </div>
        </div>
        <div className='grid w-full grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3'>
          <div className='md:col-span-2 xl:col-span-1'>
            <DatePicker
                label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.timeRange)}
                startDate={jobPostingData.startDate}
                endDate={jobPostingData.endDate}
                onChange={changeDate}
            />
          </div>
          <div>
            <div className='text-gray-500 py-1 text-xs font-medium'>
              { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.location) }
            </div>
            <TextInput
                name='jobLocation'
                value={
                  jobPostingData.location
                    ? (jobPostingData.location as string)
                    : (jobDescription.jobLocation as string)
                }
                type='text'
                onChange={(e) =>
                  setJobPostingData({ ...jobPostingData, location: e.target.value })
                }
            />
          </div>
          <div>
            <div className='text-gray-500 py-1 text-xs font-medium'>
              { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.department) }
            </div>
            <TextInput
                name='jobDepartment'
                value={jobPostingData.department as string}
                type='text'
                onChange={(e) =>
                  setJobPostingData({ ...jobPostingData, department: e.target.value })
                }
            />
          </div>
          <div>
            <div className='text-gray-500 py-1 text-xs font-medium'>
              { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.jobTitle) }
            </div>
            <AsyncCustomSelect
                defaultOptions={defaultJobTitles}
                value={
                  jobPostingData.jobTitle
                    ? {
                      value: jobPostingData.jobTitle as string,
                      label: jobPostingData.jobTitle as string,
                    }
                    : undefined
                }
                onChange={handleJobTitleChange}
                loadOptions={(inputValue, callback) =>
                  loadOptionsDebounced(inputValue, callback)
                }
                isMulti={false}
            />
          </div>
          <div>
            <div className='text-gray-500 py-1 text-xs font-medium'>
              { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.workingArrangement) }
            </div>
            <CustomSelect
                name='jobWorkType'
                value={{
                  label: jobPostingData.workType as string,
                  value: jobPostingData.workType as string,
                }}
                options={workTypes}
                onChange={handleChange}
            />
          </div>
        </div>
      </div>
      <div className='flex flex-col items-end'>
        <div className='w-full sm:w-auto'>
          <Button
              onClick={handleSave}
              variant='primary'
              label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.settings.next)}
              disabled={isSaveButtonDisabled}
              className='w-full sm:w-auto'
          />
        </div>
      </div>
    </div>
  );
};
export default JobPostSettings;
